import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchCourseLessonsLite } from "./nativeLearning";

/**
 * Course + lesson search for the Courses page.
 *
 * Courses are matched against the list already in CoursesView's state, so they
 * filter from the first keystroke. Lessons have no search endpoint and no
 * cross-course list endpoint, so the index is built client-side by fanning out
 * one lessonlist call per course. That runs lazily on first focus (never on page
 * load) and is cached per funnel, since CoursesView unmounts every time a course
 * opens. Swap buildIndex's body for a single search call when the backend grows
 * one — nothing else here needs to change.
 *
 * The matching helpers and the lesson index cache are exported: Spotlight's
 * search page ranks and highlights with the same rules and shares one index.
 */

const MAX_COURSES = 5;
const MAX_LESSONS = 8;
const RECENT_MAX = 5;
// Don't fan out across every course until the member has actually committed to a
// lesson search — focusing the box, or a single stray keystroke, shouldn't cost
// one request per course.
const MIN_QUERY = 2;
// Browsers cap ~6 concurrent requests per host, so firing 50 at once doesn't make
// them finish sooner — it just queues them and starves the rest of the page.
export const INDEX_CONCURRENCY = 6;
const INDEX_TTL_MS = 10 * 60 * 1000;

// Lesson index, cached per funnel. The in-memory map covers CoursesView
// unmounting (it unmounts on every course open); sessionStorage additionally
// covers a reload, so the fan-out is once per tab session rather than per visit.
const lessonIndexCache = new Map();
const idxKey = (funnelId, memberId) => `courseSearchIdx:${funnelId}:${memberId}`;

export function readCachedIndex(funnelId, memberId) {
    const mem = lessonIndexCache.get(funnelId);
    if (mem) return mem;
    try {
        const raw = sessionStorage.getItem(idxKey(funnelId, memberId));
        if (!raw) return null;
        const { at, lessons } = JSON.parse(raw);
        if (!at || Date.now() - at > INDEX_TTL_MS) return null;
        lessonIndexCache.set(funnelId, lessons);
        return lessons;
    } catch {
        return null;
    }
}

export function writeCachedIndex(funnelId, memberId, lessons) {
    lessonIndexCache.set(funnelId, lessons);
    try {
        sessionStorage.setItem(idxKey(funnelId, memberId), JSON.stringify({ at: Date.now(), lessons }));
    } catch {
        /* quota / private mode — the in-memory cache still applies */
    }
}

// Bounded-concurrency map. Each course reports as it lands so matches can render
// progressively instead of waiting on the slowest request in the batch.
export async function pooled(items, limit, fn) {
    let cursor = 0;
    await Promise.all(
        Array.from({ length: Math.min(limit, items.length) }, async () => {
            while (cursor < items.length) {
                const i = cursor++;
                await fn(items[i]);
            }
        })
    );
}

const DEFAULT_THUMB =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%23111827'/%3E%3C/svg%3E";

export const LESSON_ICON = {
    2: { label: "Audio", color: "#8b5cf6", d: "M9 19V6l12-2v13M9 19a3 3 0 11-6 0 3 3 0 016 0zm12-3a3 3 0 11-6 0 3 3 0 016 0z" },
    3: { label: "Video", color: "#ef4444", d: "M15 10l4.55-2.28A1 1 0 0121 8.62v6.76a1 1 0 01-1.45.9L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" },
    1: { label: "Content", color: "#0ea5e9", d: "M9 12h6m-6 4h6M9 8h6M5 4h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z" },
};

function Svg({ d, className = "w-4 h-4" }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d={d} />
        </svg>
    );
}

// Punctuation-insensitive, so "dont" matches "Don't" and "e-mail" matches "email".
export const norm = (s) =>
    String(s || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();

/**
 * Every term must match, so "int fun" narrows to "Introduction to Funnels".
 * Word-start beats mid-word so "pro" ranks "Pro Tips" above "Copywriting".
 * 0 = no match.
 */
export function score(text, terms) {
    const n = norm(text);
    if (!n) return 0;
    const words = n.split(" ");
    let total = 0;
    for (const t of terms) {
        if (n.startsWith(t)) total += 100;
        else if (words.some((w) => w.startsWith(t))) total += 60;
        else if (n.includes(t)) total += 25;
        else return 0;
    }
    return total;
}

// Wrap matched runs in brand color. Terms are matched against the raw string, so
// a term that only matched after normalisation simply isn't highlighted.
export function highlight(text, terms, primary) {
    const raw = String(text || "");
    const low = raw.toLowerCase();
    const hits = [];
    terms.forEach((t) => {
        if (!t) return;
        let i = low.indexOf(t);
        while (i !== -1) {
            hits.push([i, i + t.length]);
            i = low.indexOf(t, i + t.length);
        }
    });
    if (!hits.length) return raw;
    hits.sort((a, b) => a[0] - b[0]);
    const merged = [];
    hits.forEach(([s, e]) => {
        const last = merged[merged.length - 1];
        if (last && s <= last[1]) last[1] = Math.max(last[1], e);
        else merged.push([s, e]);
    });
    const out = [];
    let pos = 0;
    merged.forEach(([s, e], k) => {
        if (s > pos) out.push(raw.slice(pos, s));
        out.push(
            <mark key={k} className="bg-transparent font-extrabold" style={{ color: primary }}>
                {raw.slice(s, e)}
            </mark>
        );
        pos = e;
    });
    if (pos < raw.length) out.push(raw.slice(pos));
    return out;
}

// Hoisted: defining these inside CourseSearch would make React see a new
// component type each keystroke and remount every row, re-fetching thumbnails.
function Row({ item, idx, active, primary, terms, onHover, onPick }) {
    const isCourse = item.kind === "course";
    const meta = LESSON_ICON[item.lesson_type] || LESSON_ICON[1];
    return (
        <button
            type="button"
            role="option"
            aria-selected={active}
            data-idx={idx}
            onMouseEnter={() => onHover(idx)}
            onClick={() => onPick(item)}
            className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition"
            style={{ backgroundColor: active ? `${primary}0f` : "transparent" }}
        >
            {isCourse ? (
                <img
                    src={item.image || DEFAULT_THUMB}
                    onError={(e) => { e.currentTarget.src = DEFAULT_THUMB; }}
                    alt=""
                    className="h-10 w-14 shrink-0 rounded-lg object-cover bg-ink"
                />
            ) : (
                <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border"
                    style={{ color: item.locked ? "rgb(var(--c-gray-400))" : meta.color, borderColor: item.locked ? "rgb(var(--c-gray-200))" : `${meta.color}33`, backgroundColor: item.locked ? "#f9fafb" : `${meta.color}0f` }}
                >
                    <Svg d={item.locked ? "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" : meta.d} className="h-[18px] w-[18px]" />
                </span>
            )}

            <span className="min-w-0 flex-1">
                <span className={`block truncate text-[13.5px] font-semibold ${item.locked && !isCourse ? "text-gray-400" : "text-gray-900"}`}>
                    {highlight(item.title, terms, primary)}
                </span>
                <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-gray-500">
                    {isCourse ? (
                        <>
                            <span>{item.total_lessons > 0 ? `${item.total_lessons} ${item.total_lessons === 1 ? "lesson" : "lessons"}` : "No lessons yet"}</span>
                            <span className="text-gray-300 dark:text-gray-500">·</span>
                            <span className="font-semibold" style={{ color: Number(item.unlock_level) > 0 ? "#d97706" : item.access_type === 2 ? "#d97706" : item.locked ? "#6b7280" : "#16a34a" }}>
                                {Number(item.unlock_level) > 0 ? `Level ${item.unlock_level}` : item.access_type === 2 ? "Paid" : item.locked ? "Locked" : "Free"}
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="truncate">in {item.course_name}</span>
                            {item.completed && (
                                <>
                                    <span className="text-gray-300 dark:text-gray-500">·</span>
                                    <span className="font-semibold text-green-600 dark:text-green-300">Done</span>
                                </>
                            )}
                            {item.locked && (
                                <>
                                    <span className="text-gray-300 dark:text-gray-500">·</span>
                                    <span className="font-semibold text-gray-400">Locked</span>
                                </>
                            )}
                        </>
                    )}
                </span>
            </span>

            {active && (
                <span className="hidden shrink-0 items-center text-gray-300 dark:text-gray-500 sm:flex">
                    <Svg d="M9 5l7 7-7 7" className="h-4 w-4" />
                </span>
            )}
        </button>
    );
}

function GroupLabel({ children, hint }) {
    return (
        <div className="flex items-center gap-2 px-3 pb-1 pt-2.5">
            <span className="text-[10.5px] font-extrabold uppercase tracking-wider text-gray-400">{children}</span>
            {hint && <span className="text-[10.5px] text-gray-400">{hint}</span>}
        </div>
    );
}

export default function CourseSearch({ courses, primary, member, token, onPickCourse, onPickLesson, variant = "card" }) {
    // "header" matches the catalog group-search pill in the top bar; "card" is the
    // compact box used inside the Courses card header on every other template.
    const headerLook = variant === "header";
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const [lessons, setLessons] = useState(null);
    const [indexing, setIndexing] = useState(false);
    const [done, setDone] = useState(0);
    const [rect, setRect] = useState(null);

    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const startedRef = useRef(false);
    const aliveRef = useRef(true);

    const funnelId = member?.funnel_id;
    const memberId = member?.community_member_id;
    const jwt = member?.member_token;
    const recentKey = `courseSearchRecent:${token}`;

    const [recent, setRecent] = useState(() => {
        try { return JSON.parse(localStorage.getItem(recentKey) || "[]"); }
        catch { return []; }
    });

    /* ---------- lesson index (built on demand, once per session) ---------- */
    // Must re-arm on mount, not just disarm on unmount: StrictMode's simulated
    // remount runs the cleanup, which would otherwise leave this false forever
    // and silently drop every result.
    useEffect(() => {
        aliveRef.current = true;
        return () => { aliveRef.current = false; };
    }, []);

    useEffect(() => {
        const cached = funnelId ? readCachedIndex(funnelId, memberId) : null;
        if (cached) {
            setLessons(cached);
            startedRef.current = true; // already have it — never fan out again
        }
    }, [funnelId, memberId]);

    const buildIndex = useCallback(async () => {
        if (startedRef.current || !courses?.length || !funnelId) return;
        startedRef.current = true;
        setIndexing(true);
        setDone(0);
        const acc = [];
        try {
            await pooled(courses, INDEX_CONCURRENCY, async (c) => {
                let ls = [];
                try {
                    ls = await fetchCourseLessonsLite({ funnelId, memberId, jwt, courseToken: c.course_token });
                } catch {
                    ls = []; // one bad course shouldn't blank the whole index
                }
                if (!aliveRef.current) return;
                acc.push(...ls.map((l) => ({ ...l, course_name: c.name, course_image: c.image })));
                setLessons([...acc]); // stream partial matches as courses land
                setDone((n) => n + 1);
            });
            if (aliveRef.current) writeCachedIndex(funnelId, memberId, acc);
        } finally {
            if (aliveRef.current) setIndexing(false);
        }
    }, [courses, funnelId, memberId, jwt]);

    // Only once the query is a real search — never on focus.
    useEffect(() => {
        if (q.trim().length >= MIN_QUERY) buildIndex();
    }, [q, buildIndex]);

    /* ---------- results ---------- */
    const terms = useMemo(() => norm(q).split(" ").filter(Boolean), [q]);

    const results = useMemo(() => {
        if (!terms.length) return { courses: [], lessons: [] };
        const cs = (courses || [])
            .map((c) => ({ c, s: score(c.name, terms) }))
            .filter((x) => x.s > 0)
            .sort((a, b) => b.s - a.s)
            .slice(0, MAX_COURSES)
            .map(({ c }) => ({
                kind: "course",
                key: `c:${c.course_token}`,
                course_token: c.course_token,
                title: c.name,
                image: c.image,
                total_lessons: c.total_lessons,
                access_type: c.access_type,
                unlock_level: c.unlock_level,
                locked: c.locked,
            }));

        const ls = (lessons || [])
            .map((l) => ({ l, s: score(l.title, terms) }))
            .filter((x) => x.s > 0)
            .sort((a, b) => b.s - a.s)
            .slice(0, MAX_LESSONS)
            .map(({ l }) => ({
                kind: "lesson",
                key: `l:${l.course_token}:${l.lesson_token}`,
                course_token: l.course_token,
                lesson_token: l.lesson_token,
                title: l.title,
                course_name: l.course_name,
                lesson_type: l.lesson_type,
                locked: l.locked,
                completed: l.completed,
            }));
        return { courses: cs, lessons: ls };
    }, [terms, courses, lessons]);

    // A remembered pick whose course is gone (unpublished, access revoked) would
    // dead-link, so drop it from the list rather than navigating into nothing.
    const recentValid = useMemo(() => {
        const live = new Set((courses || []).map((c) => c.course_token));
        return recent.filter((r) => live.has(r.course_token));
    }, [recent, courses]);

    const showRecent = !terms.length && recentValid.length > 0;
    const flat = useMemo(
        () => (showRecent ? recentValid : [...results.courses, ...results.lessons]),
        [showRecent, recentValid, results]
    );

    useEffect(() => { setActiveIdx(0); }, [q]);

    /* ---------- panel placement (portal — the card clips overflow) ---------- */
    const place = useCallback(() => {
        const el = wrapRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const w = Math.min(Math.max(r.width, 360), window.innerWidth - 16);
        const left = Math.min(Math.max(8, r.left), Math.max(8, window.innerWidth - w - 8));
        setRect({ top: r.bottom + 8, left, width: w });
    }, []);

    useEffect(() => {
        if (!open) return;
        place();
        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, true);
        return () => {
            window.removeEventListener("resize", place);
            window.removeEventListener("scroll", place, true);
        };
    }, [open, place]);

    /* ---------- outside click ---------- */
    useEffect(() => {
        if (!open) return undefined;

        // The panel is portalled to <body>, so "inside" is two disjoint subtrees:
        // the input wrapper and the floating list.
        //
        // composedPath() is preferred over contains(): it is captured at dispatch,
        // so it still reports the truth when React has already unmounted the
        // clicked node by the time this runs, and it sees through shadow roots.
        // contains() stays as the fallback for engines without it.
        const isInside = (e) => {
            const path = typeof e.composedPath === "function" ? e.composedPath() : null;
            if (path) {
                return (
                    (!!wrapRef.current && path.includes(wrapRef.current)) ||
                    (!!listRef.current && path.includes(listRef.current))
                );
            }
            return (
                !!wrapRef.current?.contains(e.target) ||
                !!listRef.current?.contains(e.target)
            );
        };

        const onDown = (e) => {
            if (isInside(e)) return;
            setOpen(false);
        };

        /*
         * Why this is not just document.addEventListener("mousedown", …):
         *
         *  - Safari does not give a tap the same mousedown a click gets — on
         *    touch it synthesises mouse events late, and on non-interactive
         *    targets it may not deliver them to document at all. So a tap
         *    outside never reached the handler and the panel stayed open.
         *    pointerdown is one event for mouse, touch and pen, and Safari has
         *    supported it since 13; mousedown + touchstart is the fallback.
         *
         *  - Capture phase, so a stopPropagation() anywhere between the click
         *    target and document cannot leave the panel stuck open.
         *
         *  - focusin closes it when the creator tabs away rather than clicks.
         */
        const opts = { capture: true };
        const events =
            typeof window !== "undefined" && window.PointerEvent
                ? ["pointerdown", "focusin"]
                : ["mousedown", "touchstart", "focusin"];

        events.forEach((name) => document.addEventListener(name, onDown, opts));
        return () =>
            events.forEach((name) => document.removeEventListener(name, onDown, opts));
    }, [open]);

    useEffect(() => {
        const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
        if (el) el.scrollIntoView({ block: "nearest" });
    }, [activeIdx, flat]);

    /* ---------- select ---------- */
    const remember = (item) => {
        const entry =
            item.kind === "course"
                ? { kind: "course", key: item.key, course_token: item.course_token, title: item.title, image: item.image, total_lessons: item.total_lessons, access_type: item.access_type, locked: item.locked }
                : { kind: "lesson", key: item.key, course_token: item.course_token, lesson_token: item.lesson_token, title: item.title, course_name: item.course_name, lesson_type: item.lesson_type, locked: item.locked };
        setRecent((prev) => {
            const next = [entry, ...prev.filter((r) => r.key !== entry.key)].slice(0, RECENT_MAX);
            try { localStorage.setItem(recentKey, JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    };

    const pick = (item) => {
        if (!item) return;
        remember(item);
        setOpen(false);
        setQ("");
        inputRef.current?.blur();
        if (item.kind === "course") onPickCourse(item.course_token);
        // A locked lesson can't play — land on the course page, which explains why.
        else if (item.locked) onPickCourse(item.course_token);
        else onPickLesson(item.course_token, item.lesson_token);
    };

    const onKeyDown = (e) => {
        if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); return; }
        if (!open) {
            if (e.key === "ArrowDown") { setOpen(true); e.preventDefault(); }
            return;
        }
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (flat.length ? (i + 1) % flat.length : 0)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0)); }
        else if (e.key === "Enter") { e.preventDefault(); pick(flat[activeIdx]); }
    };

    const focus = () => setOpen(true);

    const rowProps = { primary, terms, onHover: setActiveIdx, onPick: pick };

    const nothing = !!terms.length && !results.courses.length && !results.lessons.length && !indexing;

    return (
        <>
            <div ref={wrapRef} className={`relative ${headerLook ? "w-full" : "w-full sm:w-64 lg:w-72"}`}>
                <span className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-gray-400 ${headerLook ? "left-3.5" : "left-3"}`}>
                    <Svg d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" className={headerLook ? "h-5 w-5" : "h-[18px] w-[18px]"} />
                </span>
                <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    onFocus={(e) => {
                        focus();
                        e.currentTarget.style.borderColor = primary;
                        e.currentTarget.style.boxShadow = `0 0 0 3px ${primary}1f`;
                    }}
                    onBlur={(e) => {
                        e.currentTarget.style.borderColor = "";
                        e.currentTarget.style.boxShadow = "";
                    }}
                    onKeyDown={onKeyDown}
                    type="text"
                    placeholder="Search courses or lessons…"
                    aria-label="Search courses or lessons"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls="course-search-results"
                    aria-autocomplete="list"
                    autoComplete="off"
                    className={headerLook
                        ? "w-full rounded-full border border-gray-200 bg-gray-50 py-2.5 pl-11 pr-9 text-sm text-gray-700 placeholder:text-gray-400 outline-none transition-colors focus:bg-surface"
                        : "w-full rounded-xl border border-gray-200 bg-surface py-2 pl-9 pr-8 text-[13px] text-gray-900 outline-none transition placeholder:text-gray-400"}
                />
                {q && (
                    <button
                        type="button"
                        onClick={() => { setQ(""); inputRef.current?.focus(); }}
                        aria-label="Clear search"
                        className={`absolute top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-gray-600 ${headerLook ? "right-3.5" : "right-2.5"}`}
                    >
                        <Svg d="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                    </button>
                )}
            </div>

            {open && rect && createPortal(
                <div
                    ref={listRef}
                    className="fixed z-[70] overflow-hidden rounded-2xl border border-gray-200 bg-surface shadow-2xl"
                    style={{ top: rect.top, left: rect.left, width: rect.width }}
                >
                    <div id="course-search-results" role="listbox" aria-label="Search results" className="max-h-[min(60vh,26rem)] overflow-y-auto thin-scroll py-1">
                        {showRecent && (
                            <>
                                <GroupLabel>Recent</GroupLabel>
                                {recentValid.map((item, i) => (
                                    <Row key={item.key} item={item} idx={i} active={i === activeIdx} {...rowProps} />
                                ))}
                            </>
                        )}

                        {!terms.length && !recentValid.length && (
                            <p className="px-3 py-6 text-center text-[12.5px] text-gray-400">
                                Start typing to find a course or a lesson.
                            </p>
                        )}

                        {!!terms.length && (
                            <>
                                {results.courses.length > 0 && (
                                    <>
                                        <GroupLabel>Courses</GroupLabel>
                                        {results.courses.map((item, i) => (
                                            <Row key={item.key} item={item} idx={i} active={i === activeIdx} {...rowProps} />
                                        ))}
                                    </>
                                )}

                                {(results.lessons.length > 0 || indexing) && (
                                    <GroupLabel hint={indexing ? `${done}/${courses.length}` : undefined}>Lessons</GroupLabel>
                                )}
                                {results.lessons.map((item, i) => (
                                    <Row
                                        key={item.key}
                                        item={item}
                                        idx={results.courses.length + i}
                                        active={results.courses.length + i === activeIdx}
                                        {...rowProps}
                                    />
                                ))}
                                {indexing && !results.lessons.length && (
                                    <p className="px-3 py-2.5 text-[12.5px] text-gray-400">Searching lessons…</p>
                                )}

                                {nothing && (
                                    <p className="px-3 py-6 text-center text-[12.5px] text-gray-400">
                                        Nothing matches “<span className="font-semibold text-gray-500">{q.trim()}</span>”.
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
